import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  MessageSquareCode,
  Database,
  Users,
  Settings,
  Bot,
  Brain,
  FileText,
  Crown,
  KeyRound,
  ShieldCheck,
  Lock,
  HeartPulse,
} from 'lucide-react';
import { Page } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPage, onNavigate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const menuItems = [
    { id: Page.DASHBOARD, label: t('sidebar.dashboard'), icon: LayoutDashboard },
    { id: Page.PLAYGROUND, label: t('sidebar.playground'), icon: MessageSquareCode },
    { id: Page.USERS, label: t('sidebar.users'), icon: Users },
    { id: Page.DAILY_CONDITIONS, label: t('sidebar.dailyConditions'), icon: HeartPulse },
    { id: Page.MEMORY, label: t('sidebar.memory'), icon: Brain, disabled: true },
    { id: Page.DOCUMENTS, label: t('sidebar.documents'), icon: FileText, disabled: true },
    { id: Page.DB_MONITOR, label: t('sidebar.dbMonitor'), icon: Database, disabled: true },
    { id: Page.TIERS, label: t('sidebar.tiers'), icon: Crown },
    { id: Page.API_KEYS, label: t('sidebar.apiKeys'), icon: KeyRound },
    ...(user?.role === 'super_admin' || user?.role === 'admin'
      ? [{ id: Page.ADMIN_ACCOUNTS, label: t('sidebar.adminAccounts'), icon: ShieldCheck }]
      : []),
    { id: Page.SETTINGS, label: t('sidebar.settings'), icon: Settings },
  ];

  return (
    <div className="w-64 bg-slate-900 text-slate-300 flex flex-col h-full border-r border-slate-800 shrink-0 transition-all duration-300">
      <div className="p-6 flex items-center gap-3 text-white border-b border-slate-800">
        <div className="bg-blue-600 p-2 rounded-lg">
          <Bot size={24} />
        </div>
        <div>
          <h1 className="font-bold text-lg tracking-tight">{t('sidebar.title')}</h1>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;
          const isDisabled = 'disabled' in item && item.disabled;
          return (
            <button
              key={item.id}
              onClick={() => !isDisabled && onNavigate(item.id)}
              disabled={isDisabled}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isDisabled
                  ? 'text-slate-600 cursor-not-allowed opacity-50'
                  : isActive
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                    : 'hover:bg-slate-800 hover:text-white'
              }`}
              title={isDisabled ? '개발 예정' : undefined}
            >
              <Icon size={18} />
              {item.label}
              {isDisabled && <Lock size={12} className="ml-auto text-slate-500" />}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;
